import { useQuery } from '@tanstack/react-query';

import { EnvironmentId } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EdgeStack } from '@/react/edge/edge-stacks/types';
import { ContainerId } from '@/react/docker/containers/types';
import { UseQueryOptions } from '@/react-tools/react-query';

import { queryKeys } from '../../queries/query-keys';

type StatusProps = {
  edgeStackId: EdgeStack['Id'];
  environmentId: EnvironmentId;
  containerId?: ContainerId;
};

export function logsStatusQueryKey({
  edgeStackId,
  environmentId,
  containerId,
}: StatusProps) {
  const envKeys = [
    ...queryKeys.item(edgeStackId),
    'logs',
    environmentId,
  ] as const;

  return containerId ? ([...envKeys, containerId] as const) : envKeys;
}

export function useLogsStatus(
  { edgeStackId, environmentId, containerId }: StatusProps,
  options?: UseQueryOptions<LogStatus>
) {
  return useQuery(
    logsStatusQueryKey({ edgeStackId, environmentId, containerId }),
    () => getLogsStatus({ edgeStackId, environmentId, containerId }),
    {
      ...options,
      refetchInterval(status) {
        if (status === 'pending') {
          return 30 * 1000;
        }

        return false;
      },
    }
  );
}

export type LogStatus = 'collected' | 'idle' | 'pending';

interface LogsStatusResponse {
  status: LogStatus;
}

async function getLogsStatus({
  edgeStackId,
  environmentId,
  containerId,
}: StatusProps) {
  try {
    const { data } = await axios.get<LogsStatusResponse>(
      `/edge_stacks/${edgeStackId}/logs/${environmentId}`,
      {
        params: { container_id: containerId },
      }
    );
    return data.status;
  } catch (error) {
    throw parseAxiosError(error as Error, 'Unable to retrieve logs status');
  }
}
